/** Contrato pensado para integrar con TikTok Insights / contenido cuando existan en el backend. */

export type TikTokTrendDir = "up" | "down" | "flat";

export interface TikTokKpiPill {
  value: number;
  labelKey: string;
  direction?: TikTokTrendDir;
}

export interface TikTokTimeSeriesDay {
  label: string;
  /** ISO date YYYY-MM-DD */
  iso: string;
  followerTotal?: number;
  postsCount?: number;
  netFollowers?: number;
  views?: number;
  interactions?: number;
  likes?: number;
  comments?: number;
  shares?: number;
}

export interface TikTokPostRow {
  id: string;
  caption: string;
  typeLabel: string;
  dateLabel: string;
  timeLabel: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  durationSeconds: number;
  /** Placeholder hasta tener miniatura desde API */
  thumbnailColor: string;
  isNew?: boolean;
}

export interface TikTokAnalyticsSnapshot {
  accountHandle: string;
  periodSinceLabel: string;
  periodUntilLabel: string;
  growth: {
    followers: TikTokKpiPill & { decimals?: number };
    posts: TikTokKpiPill;
    followersSeries: TikTokTimeSeriesDay[];
  };
  balance: {
    netPeriod: TikTokKpiPill;
    daily: TikTokTimeSeriesDay[];
  };
  postsPeriod: {
    views: TikTokKpiPill & { posts: TikTokKpiPill };
    viewsSeries: TikTokTimeSeriesDay[];
    interactions: {
      total: TikTokKpiPill;
      likes: TikTokKpiPill;
      comments: TikTokKpiPill;
      shares: TikTokKpiPill;
      posts: TikTokKpiPill;
    };
    interactionsSeries: TikTokTimeSeriesDay[];
  };
  postsTable: TikTokPostRow[];
  /** Opcional: metadatos del API (p. ej. totales de perfil). */
  meta?: {
    videoCountTotal: number;
    likesCountTotal: number;
  };
}

const apr = "2026-04";

/** Serie 13–30 abr 2026 (referencia UI). */
const daysApr13to30 = [
  {
    iso: `${apr}-13`,
    follower: 80,
    posts: 0,
    net: 0,
    views: 320,
    interactions: 1,
    likes: 1,
    comments: 0,
    shares: 0,
  },
  { iso: `${apr}-14`, follower: 80, posts: 0, net: 0, views: 280, interactions: 0, likes: 0, comments: 0, shares: 0 },
  { iso: `${apr}-15`, follower: 80, posts: 0, net: 0, views: 350, interactions: 2, likes: 2, comments: 0, shares: 0 },
  { iso: `${apr}-16`, follower: 81, posts: 1, net: 1, views: 520, interactions: 5, likes: 5, comments: 0, shares: 0 },
  {
    iso: `${apr}-17`,
    follower: 81,
    posts: 0,
    net: 0.5,
    views: 480,
    interactions: 2,
    likes: 2,
    comments: 0,
    shares: 0,
  },
  { iso: `${apr}-18`, follower: 82, posts: 0, net: 0.5, views: 410, interactions: 1, likes: 1, comments: 0, shares: 0 },
  {
    iso: `${apr}-19`,
    follower: 82,
    posts: 0,
    net: 1,
    views: 380,
    interactions: 1,
    likes: 1,
    comments: 0,
    shares: 0,
  },
  {
    iso: `${apr}-20`,
    follower: 82,
    posts: 1,
    net: 1.5,
    views: 450,
    interactions: 3,
    likes: 3,
    comments: 0,
    shares: 0,
  },
  {
    iso: `${apr}-21`,
    follower: 82,
    posts: 1,
    net: 0,
    views: 512,
    interactions: 4,
    likes: 4,
    comments: 0,
    shares: 0,
  },
  { iso: `${apr}-22`, follower: 83, posts: 0, net: 0, views: 400, interactions: 2, likes: 2, comments: 0, shares: 0 },
  { iso: `${apr}-23`, follower: 83, posts: 0, net: 0, views: 360, interactions: 1, likes: 1, comments: 0, shares: 0 },
  { iso: `${apr}-24`, follower: 83, posts: 0, net: 0, views: 340, interactions: 1, likes: 1, comments: 0, shares: 0 },
  {
    iso: `${apr}-25`,
    follower: 83,
    posts: 1,
    net: 0,
    views: 560,
    interactions: 2,
    likes: 2,
    comments: 0,
    shares: 0,
  },
  { iso: `${apr}-26`, follower: 83, posts: 0, net: 0.5, views: 420, interactions: 1, likes: 1, comments: 0, shares: 0 },
  { iso: `${apr}-27`, follower: 83, posts: 0, net: 0, views: 390, interactions: 0, likes: 0, comments: 0, shares: 0 },
  {
    iso: `${apr}-28`,
    follower: 84,
    posts: 0,
    net: 1,
    views: 360,
    interactions: 0,
    likes: 0,
    comments: 0,
    shares: 0,
  },
  {
    iso: `${apr}-29`,
    follower: 84,
    posts: 1,
    net: 1.5,
    views: 510,
    interactions: 2,
    likes: 2,
    comments: 0,
    shares: 0,
  },
  {
    iso: `${apr}-30`,
    follower: 84,
    posts: 0,
    net: 0,
    views: 300,
    interactions: 0,
    likes: 0,
    comments: 0,
    shares: 0,
  },
];

function toLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d);
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${dt.getDate()} ${months[dt.getMonth()] ?? ""}`;
}

const growthSeries: TikTokTimeSeriesDay[] = daysApr13to30.map((d) => ({
  label: toLabel(d.iso),
  iso: d.iso,
  followerTotal: d.follower,
  postsCount: d.posts,
}));

const balanceDaily: TikTokTimeSeriesDay[] = daysApr13to30.map((d) => ({
  label: toLabel(d.iso),
  iso: d.iso,
  netFollowers: d.net,
}));

const viewsInteractionsSeries: TikTokTimeSeriesDay[] = daysApr13to30.map((d) => ({
  label: toLabel(d.iso),
  iso: d.iso,
  postsCount: d.posts,
  views: d.views,
  interactions: d.interactions,
  likes: d.likes,
  comments: d.comments,
  shares: d.shares,
}));

export const tiktokAnalyticsMock: TikTokAnalyticsSnapshot = {
  accountHandle: "mi.mundo.en.movim",
  periodSinceLabel: "13 abr 2026",
  periodUntilLabel: "30 abr 2026",
  growth: {
    followers: {
      value: 84,
      labelKey: "app.tiktokAnalytics.followers",
      direction: "up",
      decimals: 0,
    },
    posts: { value: 5, labelKey: "app.tiktokAnalytics.posts", direction: "down" },
    followersSeries: growthSeries,
  },
  balance: {
    netPeriod: { value: 4, labelKey: "app.tiktokAnalytics.netFollowers", direction: "flat" },
    daily: balanceDaily,
  },
  postsPeriod: {
    views: {
      value: 2618,
      labelKey: "app.tiktokAnalytics.views",
      direction: "down",
      posts: { value: 5, labelKey: "app.tiktokAnalytics.posts", direction: "down" },
    },
    viewsSeries: viewsInteractionsSeries.map((x) => ({
      label: x.label,
      iso: x.iso,
      views: x.views,
      postsCount: x.postsCount,
    })),
    interactions: {
      total: { value: 22, labelKey: "app.tiktokAnalytics.interactions", direction: "down" },
      likes: { value: 22, labelKey: "app.tiktokAnalytics.likes", direction: "down" },
      comments: { value: 0, labelKey: "app.tiktokAnalytics.comments", direction: "flat" },
      shares: { value: 0, labelKey: "app.tiktokAnalytics.shares", direction: "flat" },
      posts: { value: 5, labelKey: "app.tiktokAnalytics.posts", direction: "down" },
    },
    interactionsSeries: viewsInteractionsSeries,
  },
  postsTable: [
    {
      id: "1",
      caption: "🌍💙 Aprender a convivir es parte del día a día...",
      typeLabel: "Video",
      dateLabel: "16 abr 2026",
      timeLabel: "11:45",
      views: 598,
      likes: 5,
      comments: 0,
      shares: 0,
      durationSeconds: 42,
      thumbnailColor: "#E50914",
      isNew: true,
    },
    {
      id: "2",
      caption: "Tips para tu rutina ♻️ #sostenible",
      typeLabel: "Video",
      dateLabel: "21 abr 2026",
      timeLabel: "09:12",
      views: 512,
      likes: 4,
      comments: 0,
      shares: 0,
      durationSeconds: 58,
      thumbnailColor: "#FFFFFF",
      isNew: true,
    },
    {
      id: "3",
      caption: "Un momento para desconectar 🌿",
      typeLabel: "Video",
      dateLabel: "20 abr 2026",
      timeLabel: "18:03",
      views: 480,
      likes: 3,
      comments: 0,
      shares: 0,
      durationSeconds: 35,
      thumbnailColor: "#010101",
    },
    {
      id: "4",
      caption: "Lo que nadie cuenta del día a día",
      typeLabel: "Video",
      dateLabel: "25 abr 2026",
      timeLabel: "14:20",
      views: 455,
      likes: 4,
      comments: 0,
      shares: 0,
      durationSeconds: 61,
      thumbnailColor: "#E50914",
    },
    {
      id: "5",
      caption: "Hoy desde otro ángulo ✨",
      typeLabel: "Video",
      dateLabel: "29 abr 2026",
      timeLabel: "10:08",
      views: 419,
      likes: 3,
      comments: 0,
      shares: 0,
      durationSeconds: 27,
      thumbnailColor: "#FFFFFF",
      isNew: true,
    },
  ],
};

export function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
